import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAdvices,
  getAdviceCategories,
  updateAdviceCategories,
  updateAdviceSections,
  updateAdviceTitle,
  AdviceProps,
} from '@/api/adviceService';
import { queryKeys } from '@/lib/queryKeys';
import { toaster } from '@/components/ui/toaster';

export const useAdminVideos = () => {
  const queryClient = useQueryClient();

  const { data: advices = [], isLoading } = useQuery<AdviceProps[]>({
    queryKey: queryKeys.adminAdvices,
    queryFn: () => getAdvices(),
  });

  const { data: allCategories = [] } = useQuery<string[]>({
    queryKey: queryKeys.adminAdviceCategories,
    queryFn: () => getAdviceCategories(),
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.adminAdvices });
    queryClient.invalidateQueries({ queryKey: queryKeys.adminAdviceCategories });
    queryClient.invalidateQueries({ queryKey: ['advices'] });
    queryClient.invalidateQueries({ queryKey: ['advice-categories'] });
  };

  const updateTitleMutation = useMutation({
    mutationFn: ({ adviceId, title }: { adviceId: string; title: string }) =>
      updateAdviceTitle(adviceId, title),
    onSuccess: () => {
      invalidateAll();
      toaster.create({ title: 'Titre mis à jour', type: 'success' });
    },
    onError: (error) => {
      toaster.create({
        title: 'Erreur lors de la mise à jour du titre',
        description: error instanceof Error ? error.message : 'Une erreur est survenue',
        type: 'error',
      });
    },
  });

  const updateCategoriesMutation = useMutation({
    mutationFn: ({ adviceId, categories }: { adviceId: string; categories: string[] }) =>
      updateAdviceCategories(adviceId, categories),
    onSuccess: () => {
      invalidateAll();
      toaster.create({ title: 'Catégories mises à jour', type: 'success' });
    },
    onError: (error) => {
      toaster.create({
        title: 'Erreur lors de la mise à jour des catégories',
        description: error instanceof Error ? error.message : 'Une erreur est survenue',
        type: 'error',
      });
    },
  });

  const updateSectionsMutation = useMutation({
    mutationFn: ({ adviceId, sections }: { adviceId: string; sections: string[] }) =>
      updateAdviceSections(adviceId, sections),
    onSuccess: () => {
      invalidateAll();
      toaster.create({ title: 'Sections mises à jour', type: 'success' });
    },
    onError: (error) => {
      toaster.create({
        title: 'Erreur lors de la mise à jour des sections',
        description: error instanceof Error ? error.message : 'Une erreur est survenue',
        type: 'error',
      });
    },
  });

  return {
    advices,
    allCategories,
    isLoading,
    updateTitle: updateTitleMutation.mutateAsync,
    updateCategories: updateCategoriesMutation.mutateAsync,
    updateSections: updateSectionsMutation.mutateAsync,
    isUpdating:
      updateTitleMutation.isPending ||
      updateCategoriesMutation.isPending ||
      updateSectionsMutation.isPending,
  };
};
